import React, { useContext, useState, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { useLocalObservable } from "mobx-react-lite";
import environment from "@environment";
import { useEffectOnce } from "@lib-shared/hooks";
import { BusinessPage } from "core/constants";
import { AppContext } from "core/context";
import { SelfServiceContext } from "core/context/self-service.context";
import { sessionManager, tokenCheckInterval } from "core/instances";
import { LocalStorageHelper } from "core/lib/local-storage-helper";
import { ModalAlert } from "core/lib/show-modal";
import signalRHelper from "core/lib/signal-r-helper";
import UrlUtils from "core/lib/url-utils";
import User, { getUser } from "core/models/user";
import Footer from "../components/footer";
import Header from "../components/header";
import InboxLayout from "./inbox.layout";
import SelfServiceHeader from "../components/self-service-header";
import PaymentLayout from "./payment.layout";
import Dashboard from "../self-service/dashboard/dashboard";
import StatementsAndInvoices from "self-service/statements-and-invoices/statements-and-invoices";
import MahForm from "../self-service/support/mah/mah-form";
import SupportLayout from "./support.layout";
import UsersLayout from "./users.layout";

//useEffect & useEffectOnce displaying multiple modal alerts
class AccessCheckState {
  isDisplayingModal = false;
}

const redirectToLogin = () => {
  let returnUrl = window.location.hash.substring(1);
  returnUrl = !returnUrl ? BusinessPage.dashboard : returnUrl;
  LocalStorageHelper.clearLog();
  LocalStorageHelper.stampedLog(`Redirect To Login. Return url ${returnUrl}`);
  sessionManager.signIn(returnUrl);
}

const MainLayout = () => {
  const appContext = useContext(AppContext);
  const selfServiceContext = useContext(SelfServiceContext);
  const cancellationToken = axios.CancelToken.source();
  const [hasAccess, setHasAccess] = useState<boolean | undefined>(undefined);
  const location = useLocation();

  console.log("Render");

  const accessState = useLocalObservable(() => new AccessCheckState());

  const checkAccess = async (showModal = true) => {
    if (hasAccess === false || sessionManager.isRefreshingToken) {
      return;
    }

    LocalStorageHelper.stampedLog("Check Access");

    const access = await sessionManager.hasAccessAsync();

    setHasAccess(access);

    if (!access) {
      LocalStorageHelper.stampedLog("Access expired");

      if (!showModal) {
        accessState.isDisplayingModal = false;
        redirectToLogin();
        return false;
      }

      if (!accessState.isDisplayingModal) {
        ModalAlert.alert("Authentication Required", "You need to sign in", {
          onClose: redirectToLogin,
          showClose: false
        });
      }

      accessState.isDisplayingModal = true;

      return false;
    }

    accessState.isDisplayingModal = false;

    if (!User.instance) {
      getUser();
    }

    return true;
  }

  useEffectOnce(() => {
    async function onLoad() {
      const hasAccess = await checkAccess();

      if (!hasAccess) {
        return;
      }

      if (!appContext.user) {
        appContext.initializeUser();
      }

      const multipleCompanies = User.instance?.hasMultipleCompanies ?? false;
      const countryCode = appContext.user?.countryCode ?? UrlUtils.getCountryCode();
      const bodyCss = `country-${countryCode}` + (multipleCompanies ? " multi" : "");
      appContext.updateCoreBodyCssName(bodyCss);

      selfServiceContext.managers.subscriptions.populateAsync();

      // const id = setInterval(checkAccess, tokenCheckInterval);
      //TEMPORARY UNTIL CONNECT FIXED

      // LocalStorageHelper.stampedLog(`ClearInterval ${id}: checkAccess`);

      signalRHelper.attachPermissionDataRefreshEvent(appContext);

      return () => {
        console.log("Cancel all API requests");
        signalRHelper.removePermissionUpdateEvent();
        cancellationToken.cancel();
      };
    }

    onLoad();
  });

  useEffect(() => {
    const checkAccessAsync = async () => {
      await checkAccess();
    }

    checkAccessAsync();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  if (!hasAccess) {
    return null;
  }

  const path = (page: string) => page.substring(1);

  const allPath = (page: string) => `${path(page)}/*`;

  // const SupportLayout = lazy(() => import("./support.layout"))
  // const UsersLayout = lazy(() => import("./users.layout"))

  return (
    <>
      <Header />

      <div className="main-container">
        <SelfServiceHeader />

        <Routes>
          <Route path={path(BusinessPage.dashboard)} element={<Dashboard />} />
          <Route path="" element={<Dashboard />} />
          <Route path="inbox/*" element={<InboxLayout />} />
          <Route
            path={allPath(BusinessPage.support)}
            element={appContext.user?.isSouthAfricanCustomer
              //? <Suspense><SupportLayout /></Suspense>
              ? <SupportLayout />
              : <MahForm />}
          />

          {/* Can use to locally test MAH form using ZA user  */}
          <Route
            path="support-mah"
            element={environment.production
              ? null
              : <MahForm />}
          />

          <Route path={path(BusinessPage.statementsAndInvoices)} element={<StatementsAndInvoices dataManager={selfServiceContext.managers.statements} />} />

          <Route path={allPath(BusinessPage.payment)} element={<PaymentLayout />} />

          {/* <Route path={allPath(BusinessPage.users)} element={<Suspense><UsersLayout /></Suspense>} /> */}
          <Route path={allPath(BusinessPage.users)} element={<UsersLayout />} />

          {/* <Route path={allPath(BusinessPage.subscriptions)} element={<Subscriptions subscriptionStore={selfServiceContext.stores.subscriptions} />} /> */}

          <Route path="" element={<Dashboard />} />
        </Routes>
      </div>

      <Footer conditionalDisplay={true} />
    </>
  );
}

export default MainLayout;
